import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '@modules/auth';
import { fetchCustomers, createCustomer, CustomerFormModal, type CustomerCreateInput } from '@modules/customer';
import { fetchQuote, fetchQuoteItems } from '@modules/quote';
import { QuoteItemOpMenu } from '@modules/quote/components/QuoteItemOpMenu';
import { useUnit } from '@modules/master/hooks/useUnit';
import { fetchLimitTypes, fetchPaymentDates, fetchEmployees } from '@modules/master';
import { Button, OrderNoInput, SelectMenu, AiReviewSection } from '@shared/components';
import { OrderDocumentTabs } from './OrderDocumentTabs';
import { nextDocumentBranchNumber, toHalfWidth, findIncompleteLineItemIndex, incompleteLineItemMessage } from '@shared/utils';
import {
  fetchPurchaseOrder,
  createPurchaseOrder,
  updatePurchaseOrder,
  type PurchaseOrderCreateInput,
} from '../services/purchase-order.service';
import { fetchOrder, fetchOrders } from '../services/order.service';
import type { TaxCalcMode, EmbeddedFormConfig, Customer, LimitType, PaymentDate, PartnerSnapshot, Order, Employee } from '@shared/types';

/** AIによる変更箇所パネルでフィールド名を日本語表示するためのラベル。 */
const PURCHASE_ORDER_FIELD_LABELS: Record<string, string> = {
  type: '種別',
  partnerId: '取引先',
  orderId: '案件',
  assignedUserId: '担当者',
  deliveryDate: '納品予定日',
  lineItems: '明細',
  subtotal: '小計',
  taxAmount: '消費税',
  total: '合計（税込）',
  notes: '備考',
};

// ─── スタイル定数 ──────────────────────────────────────────────────────────────
const cls = 'w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500';

// 単価は値引き（マイナス）入力を許容する。入力途中の "-" や空文字は 0 とみなす。
function parsePrice(s: string): number {
  return s === '' || s === '-' ? 0 : Number(s);
}
const priceVal = (v: number): string => (v === 0 ? '' : String(v));
const readCls = 'w-full border border-gray-200 rounded px-2 py-1 text-sm bg-gray-50 text-gray-600 min-h-[28px]';
const LBL = 'bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600 border-r border-gray-100 whitespace-nowrap align-middle';
const VAL = 'px-2 py-1.5 align-middle';
const TH = 'py-2 px-3 text-left text-xs font-semibold text-gray-600 border-b border-gray-200';

// ─── 明細行型 ─────────────────────────────────────────────────────────────────
interface LineItem {
  name: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  taxType: 'standard' | 'reduced' | 'exempt';
  sortOrder: number;
}

type TaxType = 'standard' | 'reduced' | 'exempt';

// 見積明細と同じ税率（非課税）プルダウンの選択肢・表示ラベル
const TAX_OPTIONS: { value: TaxType; label: string }[] = [
  { value: 'standard', label: '10%' },
  { value: 'reduced', label: '8%' },
  { value: 'exempt', label: '非課税' },
];
const taxLabel = (t: TaxType): string => TAX_OPTIONS.find((o) => o.value === t)?.label ?? '';

/** 入力欄右端に表示する下向きシェブロン（単位・税率の選択ボタン共通。見積明細と統一）。 */
function ChevronDown() {
  return (
    <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
    </svg>
  );
}

// 客先注文書テーブルのデフォルト表示行数（見積書・納品書・請求書と同じ値に揃える）
const MIN_ITEM_ROWS = 15;

function makeEmptyItem(index: number): LineItem {
  return { name: '', quantity: 0, unit: '', unitPrice: 0, taxType: 'standard', sortOrder: index };
}

const INIT_ITEMS: LineItem[] = Array.from({ length: MIN_ITEM_ROWS }, (_, i) => makeEmptyItem(i));

const NEW_ITEM: LineItem = makeEmptyItem(0);

/** items を最低 MIN_ITEM_ROWS 行まで空行でパディングする（他帳票の表示行数に合わせる）。 */
function padItems(items: LineItem[]): LineItem[] {
  if (items.length >= MIN_ITEM_ROWS) return items;
  const fillers = Array.from({ length: MIN_ITEM_ROWS - items.length }, (_, i) =>
    makeEmptyItem(items.length + i),
  );
  return [...items, ...fillers];
}

const isBlankItem = (it: LineItem): boolean =>
  it.name.trim() === '' && it.quantity === 0 && it.unitPrice === 0;

/**
 * 末尾の空白行のみ除去し、中間の空白行は帳票レイアウト用に保持する（見積書と同じ挙動）。
 * これにより継承時に空白行が詰められず、レイアウトがそのまま引き継がれる。
 */
function stripTrailingBlanks(items: LineItem[]): LineItem[] {
  let lastFilled = -1;
  for (let i = items.length - 1; i >= 0; i--) {
    if (!isBlankItem(items[i])) {
      lastFilled = i;
      break;
    }
  }
  return lastFilled < 0 ? [] : items.slice(0, lastFilled + 1);
}

// ─── ユーティリティ ───────────────────────────────────────────────────────────
function yen(n: number): string { return n.toLocaleString() + '円'; }

function toStr(ts: Timestamp | null | undefined): string {
  if (!ts || typeof ts.toDate !== 'function') return '';
  return ts.toDate().toISOString().split('T')[0];
}

function fromStr(s: string): Timestamp | null {
  return s ? Timestamp.fromDate(new Date(s)) : null;
}

function roundByMode(value: number, mode: TaxCalcMode): number {
  if (mode === 'ceil') return Math.ceil(value);
  if (mode === 'round') return Math.round(value);
  return Math.floor(value);
}

interface Totals { subtotal: number; taxAmount: number; exempt: number; grandTotal: number; }

function calcTotals(items: LineItem[], mode: TaxCalcMode): Totals {
  let subtotal = 0; let tax = 0; let exempt = 0;
  for (const item of items) {
    const s = item.unitPrice * item.quantity;
    subtotal += s;
    if (item.taxType === 'standard') tax += roundByMode(s * 0.1, mode);
    else if (item.taxType === 'reduced') tax += roundByMode(s * 0.08, mode);
    else exempt += s;
  }
  return { subtotal, taxAmount: tax, exempt, grandTotal: subtotal + tax };
}

// ─── SectionBox ──────────────────────────────────────────────────────────────
interface SectionBoxProps { title: string; children: React.ReactNode; }
function SectionBox({ title, children }: SectionBoxProps) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-50 border-b border-gray-200 px-4 py-2">
        <p className="text-sm font-semibold text-gray-600">{title}</p>
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  );
}

// ─── Field（縦積み）──────────────────────────────────────────────────────────
interface FieldProps { label: string; required?: boolean; children: React.ReactNode; }
function Field({ label, required, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-gray-600">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

// ─── 明細行コンポーネント ──────────────────────────────────────────────────────
interface ItemRowProps {
  item: LineItem;
  index: number;
  total: number;
  unitOptions: string[];
  onChange: (i: number, p: Partial<LineItem>) => void;
  onInsertAbove: (i: number) => void;
  onSwapUp: (i: number) => void;
  onSwapDown: (i: number) => void;
  onDeleteMultiple: (i: number, count: number) => void;
  onDelete: (i: number) => void;
}
function ItemRow({
  item, index, total, unitOptions, onChange,
  onInsertAbove, onSwapUp, onSwapDown, onDeleteMultiple, onDelete,
}: ItemRowProps) {
  const subtotal = item.unitPrice * item.quantity;
  const [showUnitPicker, setShowUnitPicker] = useState(false);
  const [showTaxPicker, setShowTaxPicker] = useState(false);
  const unitRef = useRef<HTMLDivElement>(null);
  const taxRef = useRef<HTMLDivElement>(null);

  // 単価入力の表示文字列（マイナス入力途中の "-" を保持するためローカル管理）。
  const [priceText, setPriceText] = useState<string>(() => priceVal(item.unitPrice));
  useEffect(() => {
    if (parsePrice(priceText) !== item.unitPrice) setPriceText(priceVal(item.unitPrice));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.unitPrice]);

  useEffect(() => {
    if (!showUnitPicker && !showTaxPicker) return;
    const handleClick = (e: MouseEvent) => {
      if (unitRef.current && !unitRef.current.contains(e.target as Node)) setShowUnitPicker(false);
      if (taxRef.current && !taxRef.current.contains(e.target as Node)) setShowTaxPicker(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showUnitPicker, showTaxPicker]);

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="px-2 py-1"><input value={item.name} onChange={(e) => onChange(index, { name: e.target.value })} className={cls} placeholder="" /></td>
      <td className="px-2 py-1 w-24"><input type="text" inputMode="numeric" value={item.quantity || ''} onChange={(e) => { const raw = toHalfWidth(e.target.value).replace(/[^\d]/g, ''); onChange(index, { quantity: raw === '' ? 0 : Number(raw) }); }} className={cls + ' text-right'} /></td>
      <td className="px-2 py-1 w-20">
        <div ref={unitRef} className="relative">
          <input value={item.unit} onChange={(e) => onChange(index, { unit: e.target.value })} placeholder="" className={cls + ' pr-6'} />
          <button type="button" onClick={() => setShowUnitPicker((v) => !v)} title="単位を選択" className="absolute inset-y-0 right-1.5 flex items-center text-gray-400 hover:text-blue-600"><ChevronDown /></button>
          {showUnitPicker && (
            <SelectMenu
              items={unitOptions.map((u) => ({
                key: u,
                label: u,
                onSelect: () => { onChange(index, { unit: u }); setShowUnitPicker(false); },
              }))}
            />
          )}
        </div>
      </td>
      <td className="px-2 py-1 w-32"><input type="text" inputMode="numeric" value={priceText} onChange={(e) => { const raw = toHalfWidth(e.target.value).replace(/[^\d-]/g, '').replace(/(?!^)-/g, ''); setPriceText(raw); onChange(index, { unitPrice: parsePrice(raw) }); }} className={cls + ' text-right'} /></td>
      <td className="px-2 py-1 w-32"><div className="border border-gray-300 rounded px-2 py-1 text-sm text-right text-gray-700 tabular-nums bg-gray-50">{subtotal !== 0 ? subtotal.toLocaleString() : '—'}</div></td>
      <td className="px-2 py-1 w-28">
        <div ref={taxRef} className="relative">
          <div onClick={() => setShowTaxPicker((v) => !v)} className={cls + ' pr-6 bg-white cursor-pointer select-none flex items-center'}>{taxLabel(item.taxType)}</div>
          <button type="button" onClick={() => setShowTaxPicker((v) => !v)} title="税率を選択" className="absolute inset-y-0 right-1.5 flex items-center text-gray-400 hover:text-blue-600"><ChevronDown /></button>
          {showTaxPicker && (
            <SelectMenu
              items={TAX_OPTIONS.map((o) => ({
                key: o.value,
                label: o.label,
                onSelect: () => { onChange(index, { taxType: o.value }); setShowTaxPicker(false); },
              }))}
            />
          )}
        </div>
      </td>
      <td className="px-2 py-1 w-14 text-center">
        <QuoteItemOpMenu index={index} total={total} onInsertAbove={() => onInsertAbove(index)} onSwapUp={() => onSwapUp(index)} onSwapDown={() => onSwapDown(index)} onDeleteMultiple={(count) => onDeleteMultiple(index, count)} onDeleteRow={() => onDelete(index)} />
      </td>
    </tr>
  );
}

// ─── フォームデータ型 ─────────────────────────────────────────────────────────
interface FormData {
  type: 'outbound' | 'inbound';
  partnerId: string;
  partnerName: string;
  orderId: string;
  orderDate: Timestamp;
  deliveryDate: Timestamp | null;
  assignedUserId: string;
  notes: string;
  taxCalcMode: TaxCalcMode;
}

// ─── 注文先ブロック ────────────────────────────────────────────────────────────
// 見積書作成の「見積先」住所ブロック（QuoteFormCustomer）と全く同じ欄・レイアウトで表示する。
// 継承時は継承元見積の partnerSnapshot を優先し、無い項目はライブ顧客レコードにフォールバックする。
interface PurchaseOrderPartnerProps {
  /** ブロック見出し。弊社発行=「注文先」/ 客先発行（受領）=「注文元」 */
  partnerLabel: string;
  partnerName: string;
  snapshot: PartnerSnapshot | null;
  selectedCustomer: Customer | null;
  limitTypes: LimitType[];
  paymentDates: PaymentDate[];
  inheritable: boolean;
  isInherit: boolean;
  /** 弊社発行（注文先=外注先/仕入先を選択）モードか。true なら取引先名はセレクタ表示。 */
  showSelect: boolean;
  /** 選択候補（顧客管理の外注先・仕入先に絞り込んだ取引先）。 */
  partnerCustomers: Customer[];
  partnerId: string;
  onSelectPartner: (id: string) => void;
  onCreateNew: () => void;
  onPartnerNameChange: (v: string) => void;
  onSwitchToInherit: () => void;
  onSwitchToNew: () => void;
}

function PurchaseOrderPartner({
  partnerLabel, partnerName, snapshot, selectedCustomer, limitTypes, paymentDates,
  inheritable, isInherit, showSelect, partnerCustomers, partnerId,
  onSelectPartner, onCreateNew, onPartnerNameChange, onSwitchToInherit, onSwitchToNew,
}: PurchaseOrderPartnerProps) {
  const c = selectedCustomer;
  // 継承元の見積先スナップショット（フル情報）を優先表示し、取得できない項目はライブ顧客へフォールバックする
  const snap = isInherit ? snapshot : null;
  const limitName = limitTypes.find((l) => l.id === c?.limitTypeId)?.name ?? '';
  const payName = paymentDates.find((p) => p.id === c?.paymentDateId)?.name ?? '';
  const dPhone = snap?.tel ?? c?.phone ?? '';
  // 空文字をスキップしてフォールバックする（partnerName が '' の編集時にライブ顧客名 c.name へ正しく落ちるよう || を使う）。
  const dName = snap?.name || partnerName || c?.name || '';
  const dFuri = snap?.furi ?? c?.nameKana ?? '';
  const dCode = snap?.masterCode ?? c?.code ?? '';
  const dZip = snap?.zip ?? c?.postalCode ?? '';
  const dPref = snap?.prefecture ?? c?.prefecture ?? '';
  const addr1 = snap?.addr1 ?? c?.addr1 ?? '';
  const addr2 = snap?.addr2 ?? c?.addr2 ?? '';
  const addr3 = snap?.addr3 ?? c?.addr3 ?? '';
  const dDept = snap?.organize ?? c?.department ?? '';
  const dLimit = snap?.limitType ?? limitName;
  const dPay = snap?.paymentDate ?? payName;
  const dFax = snap?.fax ?? c?.fax ?? '';

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 flex items-center gap-4">
        <div className="flex items-center gap-1">
          <span className="text-red-400 text-base leading-none">🔗</span>
          <p className="text-sm font-semibold text-gray-600">{partnerLabel}</p>
        </div>
        {/* 顧客継承=継承元の注文先を継承（デフォルト） / 継承解除=継承データを破棄し全欄を空白にする（販管と同じ配置） */}
        {inheritable && (
          <div className="flex items-center gap-4 text-xs text-gray-600">
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="radio" name="po-customer-inherit" checked={isInherit} onChange={onSwitchToInherit} className="accent-blue-600" />
              <span>顧客継承</span>
            </label>
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="radio" name="po-customer-inherit" checked={!isInherit} onChange={onSwitchToNew} className="accent-blue-600" />
              <span>継承解除</span>
            </label>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse table-fixed">
          <colgroup>
            <col className="w-[12%]" /><col className="w-[13%]" />
            <col className="w-[12%]" /><col className="w-[13%]" />
            <col className="w-[12%]" /><col className="w-[13%]" />
            <col className="w-[12%]" /><col className="w-[13%]" />
          </colgroup>
          <tbody>
            {/* Row 1: 取引先名 / フリガナ / 本社ID / 〒 */}
            <tr className="border-b border-gray-100">
              <td className={LBL}>取引先名 <span className="text-red-500">*</span></td>
              <td className={VAL}>
                {isInherit ? (
                  <div className={readCls}>{dName}</div>
                ) : showSelect ? (
                  // 弊社発行: 顧客管理の外注先・仕入先から注文先を選択する（＋新規登録も可）
                  <div className="flex gap-1.5">
                    <select value={partnerId} onChange={(e) => onSelectPartner(e.target.value)} className={cls}>
                      <option value="">選択してください</option>
                      {partnerCustomers.map((pc) => (
                        <option key={pc.id} value={pc.id}>{pc.name}</option>
                      ))}
                    </select>
                    <button type="button" onClick={onCreateNew} title="取引先を新規登録" className="shrink-0 px-2 rounded border border-gray-300 text-xs text-blue-600 hover:bg-blue-50 whitespace-nowrap">＋新規</button>
                  </div>
                ) : (
                  <input value={partnerName} onChange={(e) => onPartnerNameChange(e.target.value)} className={cls} placeholder="取引先名" />
                )}
              </td>
              <td className={LBL}>フリガナ</td>
              <td className={VAL}><div className={readCls}>{dFuri}</div></td>
              <td className={LBL}>本社ID（省略可）</td>
              <td className={VAL}><div className={readCls}>{dCode}</div></td>
              <td className={LBL}>〒</td>
              <td className={VAL}><div className={readCls}>{dZip}</div></td>
            </tr>
            {/* Row 2: 都道府県 / 住所(市区町村) / 住所(番地) / 住所(建物名・フロア) */}
            <tr className="border-b border-gray-100">
              <td className={LBL}>都道府県</td>
              <td className={VAL}><div className={readCls}>{dPref}</div></td>
              <td className={LBL}>住所（市区町村）</td>
              <td className={VAL}><div className={readCls}>{addr1}</div></td>
              <td className={LBL}>住所（番地）</td>
              <td className={VAL}><div className={readCls}>{addr2}</div></td>
              <td className={LBL}>住所（建物名・フロア）</td>
              <td className={VAL}><div className={readCls}>{addr3}</div></td>
            </tr>
            {/* Row 3: 部署 / 請求締日 / 支払日 / TEL */}
            <tr className="border-b border-gray-100">
              <td className={LBL}>部署</td>
              <td className={VAL}><div className={readCls}>{dDept}</div></td>
              <td className={LBL}>請求締日</td>
              <td className={VAL}><div className={readCls}>{dLimit}</div></td>
              <td className={LBL}>支払日</td>
              <td className={VAL}><div className={readCls}>{dPay}</div></td>
              <td className={LBL}>TEL</td>
              <td className={VAL}><div className={readCls}>{dPhone}</div></td>
            </tr>
            {/* Row 4: FAX /（空）/（空）/（空）（取引先IDは内部保持のみ・住所ブロックには非表示） */}
            <tr>
              <td className={LBL}>FAX</td>
              <td className={VAL}><div className={readCls}>{dFax}</div></td>
              <td className={LBL}></td>
              <td className={VAL}></td>
              <td className={LBL}></td>
              <td className={VAL}></td>
              <td className={LBL}></td>
              <td className={VAL}></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── PurchaseOrderFormPage ─────────────────────────────────────────────────────
export function PurchaseOrderFormPage({ embedded }: { embedded?: EmbeddedFormConfig } = {}) {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // 埋め込み表示時は ID・state・遷移をすべて embedded から取得する
  const id = embedded ? embedded.id : params.id;
  const navState = embedded ? embedded.state : location.state;
  const isEdit = Boolean(id);
  // 親受注のコンテキスト有無。案件詳細・見積継承から開いた場合は orderId/quoteId が渡され、
  // 関連受注番号は継承済み（読取専用）。いずれも無い注文書一覧からの単独新規作成のみ受注選択を許可する。
  const navOrderId = (navState as { orderId?: string } | null)?.orderId;
  const navQuoteId = (navState as { quoteId?: string } | null)?.quoteId;
  const allowOrderSelect = !isEdit && !navOrderId && !navQuoteId;

  const [form, setForm] = useState<FormData>({
    type: 'outbound',
    partnerId: '',
    partnerName: '',
    orderId: '',
    orderDate: Timestamp.now(),
    deliveryDate: null,
    assignedUserId: '',
    notes: '',
    taxCalcMode: 'floor',
  });
  const [items, setItems] = useState<LineItem[]>(INIT_ITEMS);
  // 注文先の継承トグル: 継承元の取引先（見積継承・編集元）と継承ON/OFF
  const [inheritedPartner, setInheritedPartner] = useState<{ partnerId: string; partnerName: string } | null>(null);
  const [inheritMode, setInheritMode] = useState<boolean>(true);
  // 注文先の住所ブロックを見積書と全く同じ内容で表示するためのデータ源:
  // 継承元見積の見積先スナップショット（フル情報）＋ライブ顧客レコード＋締日/支払日マスタ
  const [partnerSnapshot, setPartnerSnapshot] = useState<PartnerSnapshot | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [limitTypes, setLimitTypes] = useState<LimitType[]>([]);
  const [paymentDates, setPaymentDates] = useState<PaymentDate[]>([]);
  // 注文書一覧（注文管理）からの単独新規作成で親受注（案件）を選ぶための受注候補。
  // 案件詳細・見積継承から開いた場合は親受注が確定済みのため使用しない。
  const [orders, setOrders] = useState<Order[]>([]);
  // ＋新規作成で「客先発行」を選んだ場合に注文元として継承する親案件（受注）の客先スナップショット。
  const [parentOrderSnapshot, setParentOrderSnapshot] = useState<PartnerSnapshot | null>(null);
  const [parentCustomerId, setParentCustomerId] = useState<string>('');
  // 注文先（外注先/仕入先）を新規登録するモーダルの開閉。
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [docNo, setDocNo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 編集対象が「AIレビュー待ち」か（form は FormData のためレコードの印は別stateで保持）
  const [aiPending, setAiPending] = useState(false);

  const upd = useCallback((patch: Partial<FormData>) => setForm((f) => ({ ...f, ...patch })), []);

  // 住所ブロック表示用に顧客・締日/支払日マスタを読み込む（見積書作成の住所ブロックと同一の内容にするため）
  useEffect(() => {
    void fetchCustomers().then(setCustomers).catch(() => setCustomers([]));
    void fetchLimitTypes().then(setLimitTypes).catch(() => setLimitTypes([]));
    void fetchPaymentDates().then(setPaymentDates).catch(() => setPaymentDates([]));
  }, []);

  // 注文先IDに一致するライブ顧客レコード（スナップショットに無い項目のフォールバック表示に使う）
  const selectedCustomer = useMemo(
    () => customers.find((cu) => cu.id === form.partnerId) ?? null,
    [customers, form.partnerId],
  );

  // 弊社発行の注文先候補: 顧客管理のうち外注先・仕入先の区分を持つ取引先に絞り込む。
  const partnerCustomers = useMemo(
    () => customers.filter((cu) => cu.partnerTypes?.some((t) => t === 'supplier' || t === 'subcontractor')),
    [customers],
  );

  // 注文先（外注先/仕入先）の新規登録。登録後は候補を再取得し、その取引先を選択状態にする。
  const handleCreateCustomer = useCallback(async (data: CustomerCreateInput) => {
    if (!user) throw new Error('ログインが必要です');
    const created = await createCustomer(data, user.id);
    const list = await fetchCustomers();
    setCustomers(list);
    upd({ partnerId: created.id });
  }, [user, upd]);

  // 注文先の継承トグル。継承元（見積継承・編集元）がある場合のみ表示する
  const inheritable = Boolean(inheritedPartner);
  const isInherit = inheritable && inheritMode;
  // 「継承解除」→「顧客継承」: 継承元の取引先を復元する
  const switchToInherit = useCallback(() => {
    setInheritMode(true);
    if (inheritedPartner) upd({ partnerId: inheritedPartner.partnerId, partnerName: inheritedPartner.partnerName });
  }, [inheritedPartner, upd]);
  // 「顧客継承」→「継承解除」: 継承された注文先を破棄し全欄を空白にする
  const switchToNew = useCallback(() => {
    setInheritMode(false);
    upd({ partnerId: '', partnerName: '' });
  }, [upd]);

  // 注文書一覧からの単独新規作成時のみ、親受注（案件）の候補を読み込む
  useEffect(() => {
    if (!allowOrderSelect) return;
    void fetchOrders().then(setOrders).catch(() => setOrders([]));
  }, [allowOrderSelect]);

  // 親受注を選択 → 関連受注番号（orderId）のみ継承する。
  // 注文先（取引先名）は固定せず従来どおり手入力できるよう、partner 系には一切触れない。
  const handleSelectOrder = useCallback((orderId: string) => {
    setForm((prev) => ({ ...prev, orderId }));
  }, []);

  // ＋新規作成（見積継承・編集を除く）で親受注が定まっている場合、その客先スナップショットを取得しておく。
  // 「客先発行」を選んだ時に注文元（住所ブロック）として継承するために使う。
  useEffect(() => {
    if (id || navQuoteId) { setParentOrderSnapshot(null); setParentCustomerId(''); return; }
    if (!form.orderId) { setParentOrderSnapshot(null); setParentCustomerId(''); return; }
    let active = true;
    void fetchOrder(form.orderId)
      .then((o) => {
        if (!active) return;
        setParentOrderSnapshot(o?.customerSnapshot ?? null);
        setParentCustomerId(o?.customerId ?? '');
      })
      .catch(() => { if (active) { setParentOrderSnapshot(null); setParentCustomerId(''); } });
    return () => { active = false; };
  }, [id, navQuoteId, form.orderId]);

  // ＋新規作成（見積継承・編集を除く）の種別に応じて注文先IDを切り替える。
  // 弊社発行: 注文先は外注先/仕入先をユーザーが選択（partnerId をクリア）。
  // 客先発行: 注文元として親案件の客先を継承（partnerId=親案件の顧客）。
  useEffect(() => {
    if (id || navQuoteId) return;
    if (form.type === 'inbound') {
      upd({ partnerId: parentCustomerId });
    } else {
      upd({ partnerId: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navQuoteId, form.type, parentCustomerId]);

  // 案件詳細から遷移した場合は受注ID・種別（客先注文書=inbound など）を初期表示する
  useEffect(() => {
    if (id) return;
    const st = navState as { orderId?: string; type?: 'outbound' | 'inbound' } | null;
    if (!st) return;
    upd({
      ...(st.orderId ? { orderId: st.orderId } : {}),
      ...(st.type ? { type: st.type } : {}),
    });
  }, [id, navState, upd]);

  // 見積書から「客先注文書継承」で遷移した場合、見積の明細を全項目引き継ぐ（空白行も保持）
  useEffect(() => {
    if (id) return;
    const st = navState as { quoteId?: string; orderId?: string } | null;
    const quoteId = st?.quoteId;
    if (!quoteId) return;
    setLoading(true);
    void (async () => {
      try {
        const [quote, quoteItems] = await Promise.all([
          fetchQuote(quoteId),
          fetchQuoteItems(quoteId),
        ]);
        if (!quote) {
          setError('継承元の見積書が見つかりませんでした');
          return;
        }
        setInheritedPartner({ partnerId: quote.customerId, partnerName: quote.partnerSnapshot?.name ?? '' });
        // 見積先スナップショット（住所フル情報）を引き継ぎ、住所ブロックを見積書と同一内容で表示する
        setPartnerSnapshot(quote.partnerSnapshot ?? null);
        setForm((prev) => ({
          ...prev,
          orderId: st?.orderId ?? quote.orderId ?? prev.orderId,
          partnerId: quote.customerId,
          partnerName: quote.partnerSnapshot?.name ?? prev.partnerName,
          taxCalcMode: quote.taxCalcMode ?? prev.taxCalcMode,
          notes: quote.notes ?? '',
        }));
        if (quoteItems.length > 0) {
          setItems(
            padItems(
              quoteItems.map((it, idx) => ({
                name: it.name,
                quantity: it.quantity,
                unit: it.unit ?? '',
                unitPrice: it.unitPrice,
                taxType: it.taxType,
                sortOrder: idx,
              })),
            ),
          );
        }
      } catch {
        setError('見積書の継承に失敗しました');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navState]);

  // 編集時データロード
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    void fetchPurchaseOrder(id)
      .then((po) => {
        if (!po) return;
        setDocNo(po.code ?? null);
        setAiPending(po.aiPendingReview === true);
        setInheritedPartner({ partnerId: po.partnerId, partnerName: '' });
        setForm({
          type: po.type,
          partnerId: po.partnerId,
          partnerName: '',
          orderId: po.orderId ?? '',
          orderDate: Timestamp.now(),
          deliveryDate: po.deliveryDate ?? null,
          assignedUserId: po.assignedUserId ?? '',
          notes: po.notes ?? '',
          taxCalcMode: po.taxCalcMode ?? 'floor',
        });
        if (po.lineItems && po.lineItems.length > 0) {
          const loaded: LineItem[] = po.lineItems.map((li, idx) => ({
            name: li.title,
            quantity: li.qty,
            unit: li.unitId,
            unitPrice: li.unitPrice,
            taxType:
              li.taxRate === 10 ? 'standard' : li.taxRate === 8 ? 'reduced' : 'exempt',
            sortOrder: idx,
          }));
          setItems(padItems(loaded));
        }
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : '読み込みに失敗しました'))
      .finally(() => setLoading(false));
  }, [id]);

  // 新規作成（案件紐付けあり）の場合、採番予定の枝番をプレビュー表示する
  useEffect(() => {
    if (id) return;
    if (!form.orderId) { setDocNo(null); return; }
    let active = true;
    void nextDocumentBranchNumber('purchase_orders', form.orderId)
      .then((n) => { if (active) setDocNo(n); })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : '関連文書の取得に失敗しました。');
      });
    return () => { active = false; };
  }, [id, form.orderId]);

  const { subtotal, taxAmount, exempt, grandTotal } = calcTotals(items, form.taxCalcMode);

  // 単位マスタ（マスター管理 → 単位）を読み込み、明細の単位候補に反映する（見積明細と同一）
  const { units, loadUnits } = useUnit();
  useEffect(() => { void loadUnits(); }, [loadUnits]);
  useEffect(() => { void fetchEmployees().then(setEmployees).catch(() => setEmployees([])); }, []);
  const unitOptions = useMemo(
    () => [...units].sort((a, b) => a.priority - b.priority).map((u) => u.name),
    [units],
  );

  const handleItemChange = useCallback((index: number, patch: Partial<LineItem>) => {
    setItems((prev) => prev.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  }, []);

  const handleItemDelete = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index).map((it, i) => ({ ...it, sortOrder: i })));
  }, []);

  // 見積明細と同じ操作メニュー（行の挿入・並べ替え・複数行消去）
  const handleInsertAbove = useCallback((index: number) => {
    setItems((prev) => {
      const next = [...prev];
      next.splice(index, 0, { ...NEW_ITEM });
      return next.map((it, i) => ({ ...it, sortOrder: i }));
    });
  }, []);
  const handleSwap = useCallback((a: number, b: number) => {
    setItems((prev) => {
      if (a < 0 || b < 0 || a >= prev.length || b >= prev.length) return prev;
      const next = [...prev];
      [next[a], next[b]] = [next[b], next[a]];
      return next.map((it, i) => ({ ...it, sortOrder: i }));
    });
  }, []);
  const handleDeleteMultiple = useCallback((index: number, count: number) => {
    setItems((prev) => {
      const next = [...prev];
      next.splice(index, Math.max(1, count));
      return next.map((it, i) => ({ ...it, sortOrder: i }));
    });
  }, []);

  const handleAddRow = useCallback(() => {
    setItems((prev) => [...prev, { ...NEW_ITEM, sortOrder: prev.length }]);
  }, []);

  const handleSave = async () => {
    if (!user) { setError('ログインが必要です'); return; }
    // 取引先IDは手動入力しない。継承時は継承元の取引先IDを使用し、無ければサービス側で自動採番する。
    // 数量・単位・単価のいずれかが入力された行のみ必須チェック（品名のみのフリー記載行は許可）
    const invalidIndex = findIncompleteLineItemIndex(items);
    if (invalidIndex >= 0) { setError(incompleteLineItemMessage(invalidIndex)); return; }
    setSaving(true);
    setError(null);
    try {
      // 末尾の空白行のみ除去し、中間の空白行は保持する（見積書と同じ挙動・継承で詰めない）
      const lineItems = stripTrailingBlanks(items).map((it, idx) => ({
        id: String(idx),
        title: it.name,
        qty: it.quantity,
        unitId: it.unit,
        unitPrice: it.unitPrice,
        amount: it.unitPrice * it.quantity,
        taxRate: (it.taxType === 'standard' ? 10 : it.taxType === 'reduced' ? 8 : 'exempt') as
          | 10
          | 8
          | 'exempt',
      }));

      const input: PurchaseOrderCreateInput = {
        type: form.type,
        partnerId: form.partnerId,
        orderId: form.orderId || null,
        assignedUserId: form.assignedUserId || null,
        deliveryDate: form.deliveryDate,
        lineItems,
        subtotal,
        taxAmount,
        total: grandTotal,
        taxCalcMode: form.taxCalcMode,
        notes: form.notes || null,
      };
      if (isEdit && id) {
        await updatePurchaseOrder(id, input, user.id);
      } else {
        await createPurchaseOrder(input, user.id);
      }
      if (embedded) { embedded.onSaved(); return; }
      navigate('/purchase-orders');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (embedded) { embedded.onCancel(); return; }
    navigate('/purchase-orders');
  };

  // 注文先ブロックの表示モード:
  // - 見積継承/編集（inheritable）: 既存の継承トグル挙動（顧客継承/継承解除）をそのまま使う。
  // - ＋新規作成（!inheritable）: 種別で切替。弊社発行=外注先/仕入先を選択 / 客先発行=親案件の客先を継承。
  const partnerIsInherit = inheritable ? isInherit : form.type === 'inbound';
  const partnerDisplaySnapshot = inheritable ? partnerSnapshot : parentOrderSnapshot;
  const partnerShowSelect = !inheritable && form.type === 'outbound';

  if (loading) return <div className="p-6 text-gray-500">読み込み中...</div>;

  return (
    <div className={embedded ? 'space-y-4' : 'p-6 space-y-4'}>
      {!embedded && (
        <>
          <div className="flex items-baseline gap-8">
            <h1 className="text-xl font-bold text-gray-800">注文書管理</h1>
            {docNo && <span className="text-base font-semibold text-gray-500">#{docNo}</span>}
          </div>

          <nav className="text-xs text-gray-500 flex items-center gap-1">
            <Link to="/" className="hover:text-blue-600">Home</Link>
            <span>/</span>
            <Link to="/purchase-orders" className="hover:text-blue-600">注文書一覧</Link>
            <span>/</span>
            <span className="text-gray-700">{isEdit ? '注文書編集' : '注文書作成'}</span>
          </nav>

          {/* 案件に紐づく注文書は、受注一覧の詳細と同じ管理タブを表示する */}
          {form.orderId && <OrderDocumentTabs orderId={form.orderId} activeTab="purchase" />}
        </>
      )}

      {error && (
        <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>
      )}

      {/* AIレビュー待ちの注文書: 変更箇所の表示と「レビュー済みにする」（設計書 §3.5） */}
      {isEdit && id && (
        <AiReviewSection
          entityType="purchase_order"
          collectionName="purchase_orders"
          entityId={id}
          pending={aiPending}
          userId={user?.id}
          fieldLabels={PURCHASE_ORDER_FIELD_LABELS}
          onConfirmed={() => setAiPending(false)}
        />
      )}

      {/* 基本情報 */}
      <SectionBox title="基本情報">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="種別" required>
            <select value={form.type} onChange={(e) => upd({ type: e.target.value as 'outbound' | 'inbound' })} className={cls} disabled={isEdit}>
              <option value="outbound">弊社発行（委託先向け）</option>
              <option value="inbound">客先発行（受領）</option>
            </select>
          </Field>
          <Field label="発行日" required>
            <input type="date" value={toStr(form.orderDate)} onChange={(e) => upd({ orderDate: fromStr(e.target.value) ?? Timestamp.now() })} className={cls} />
          </Field>
          <Field label="納品予定日">
            <input type="date" value={toStr(form.deliveryDate)} onChange={(e) => upd({ deliveryDate: fromStr(e.target.value) })} className={cls} />
          </Field>
          <Field label="担当者">
            <select value={form.assignedUserId} onChange={(e) => upd({ assignedUserId: e.target.value })} className={cls}>
              <option value="">選択してください</option>
              {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
            </select>
          </Field>
          <Field label="関連受注番号">
            {allowOrderSelect ? (
              // 注文書一覧からの単独新規作成: 親受注（案件）を選択して受注番号を継承する
              <select value={form.orderId} onChange={(e) => handleSelectOrder(e.target.value)} className={cls}>
                <option value="">受注を選択してください</option>
                {orders.map((o) => (
                  <option key={o.id} value={o.id}>
                    {(o.orderNo || o.code || '—') + (o.title ? '　' + o.title : '')}
                  </option>
                ))}
              </select>
            ) : (
              <OrderNoInput orderId={form.orderId} onChange={(v) => upd({ orderId: v })} className={cls} />
            )}
          </Field>
        </div>
      </SectionBox>

      {/* 注文先グリッド（見積書作成の住所ブロックと全く同じ欄・レイアウト） */}
      <PurchaseOrderPartner
        partnerLabel={form.type === 'inbound' ? '注文元' : '注文先'}
        partnerName={form.partnerName}
        snapshot={partnerDisplaySnapshot}
        selectedCustomer={selectedCustomer}
        limitTypes={limitTypes}
        paymentDates={paymentDates}
        inheritable={inheritable}
        isInherit={partnerIsInherit}
        showSelect={partnerShowSelect}
        partnerCustomers={partnerCustomers}
        partnerId={form.partnerId}
        onSelectPartner={(pid) => upd({ partnerId: pid })}
        onCreateNew={() => setShowNewCustomer(true)}
        onPartnerNameChange={(v) => upd({ partnerName: v })}
        onSwitchToInherit={switchToInherit}
        onSwitchToNew={switchToNew}
      />

      {/* 明細テーブル */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-4 py-2">
          <p className="text-sm font-semibold text-gray-600">注文明細</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className={TH}>品名・摘要</th>
                <th className={TH + ' w-24'}>数量</th>
                <th className={TH + ' w-20'}>単位</th>
                <th className={TH + ' w-32'}>単価</th>
                <th className={TH + ' w-32'}>税抜料金</th>
                <th className={TH + ' w-28'}>税率</th>
                <th className="py-2 px-3 w-12 border-b border-gray-200"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <ItemRow
                  key={i}
                  item={item}
                  index={i}
                  total={items.length}
                  unitOptions={unitOptions}
                  onChange={handleItemChange}
                  onInsertAbove={handleInsertAbove}
                  onSwapUp={(idx) => handleSwap(idx, idx - 1)}
                  onSwapDown={(idx) => handleSwap(idx, idx + 1)}
                  onDeleteMultiple={handleDeleteMultiple}
                  onDelete={handleItemDelete}
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* 合計エリア */}
        <div className="border-t border-gray-200 bg-white">
          <div className="flex justify-between px-4 py-2 border-b border-gray-100">
            <span className="text-sm text-gray-700">小計</span>
            <span className="text-sm text-gray-900 tabular-nums">{yen(subtotal)}</span>
          </div>
          <div className="flex justify-between px-4 py-2 border-b border-gray-100 items-center">
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-gray-700">消費税額</span>
              <select value={form.taxCalcMode} onChange={(e) => upd({ taxCalcMode: e.target.value as TaxCalcMode })}
                className="text-xs border border-gray-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500">
                <option value="floor">切り捨て</option>
                <option value="ceil">切り上げ</option>
                <option value="round">四捨五入</option>
                <option value="manual">手動</option>
              </select>
            </div>
            <span className="text-sm text-gray-900 tabular-nums">{yen(taxAmount)}</span>
          </div>
          <div className="flex justify-between px-4 py-2 border-b border-gray-100">
            <span className="text-sm text-gray-700">非課税税額</span>
            <span className="text-sm text-gray-900 tabular-nums">{yen(exempt)}</span>
          </div>
          <div className="flex justify-between px-4 py-2">
            <span className="text-sm font-bold text-gray-900">合計</span>
            <span className="text-sm font-bold text-gray-900 tabular-nums">{yen(grandTotal)}</span>
          </div>
        </div>

        {/* ADDボタン */}
        <div className="px-4 py-3 border-t border-gray-200">
          <Button onClick={handleAddRow}>
            ADD
          </Button>
        </div>
      </div>

      {/* 備考 */}
      <SectionBox title="備考（メモ）">
        <textarea value={form.notes} onChange={(e) => upd({ notes: e.target.value })}
          rows={3} className={cls} placeholder="備考・メモを入力してください" />
      </SectionBox>

      {/* 保存/キャンセル */}
      <div className="flex justify-center gap-4 py-4">
        <Button variant="secondary" onClick={handleCancel}>
          キャンセル
        </Button>
        <Button disabled={saving} onClick={() => void handleSave()}>
          {saving ? '保存中...' : '保存'}
        </Button>
      </div>

      {/* 注文先（外注先/仕入先）の新規登録モーダル */}
      <CustomerFormModal
        isOpen={showNewCustomer}
        onClose={() => setShowNewCustomer(false)}
        onSubmit={handleCreateCustomer}
      />
    </div>
  );
}
